import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { LibraryOption } from '@/data/sources-library';
import {
  DATE_FIELDS, DATE_PRESETS, SORT_OPTIONS, SOURCE_OPTIONS, STATUS_OPTIONS, VIEWS, WP_OPTIONS,
  channelName, recentMonths, targetLabel,
  type DayParts, type LibraryFilters, type LibraryView,
} from './libraryModel';

/* Sticky console bar: saved views with counts, search, filters, layout toggle,
 * active-filter chips. Desktop shows every control inline; at 760px and below a
 * "Filters (n)" button opens the same controls in a bottom sheet. */

const control = 'h-9 min-w-0 rounded-md border border-border-default bg-surface px-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

export interface FilterOptions { target_pages: LibraryOption[]; keywords: LibraryOption[]; channels: LibraryOption[] }

function Labeled({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-1', className)}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-text-secondary">{label}</span>
      {children}
    </label>
  );
}

function withCurrent(opts: LibraryOption[], current: string): LibraryOption[] {
  return current && !opts.some((o) => o.value === current) ? [{ value: current, count: 0 }, ...opts] : opts;
}

/** The filter controls, shared by the desktop row and the mobile sheet. */
export function FilterControls({ f, set, options, today, stacked }: {
  f: LibraryFilters; set: (p: Partial<LibraryFilters>) => void; options: FilterOptions; today: DayParts; stacked?: boolean;
}) {
  const months = recentMonths(today, 24);
  const one = (arr: string[]) => (arr.length === 1 ? arr[0] : '');
  const wrap = stacked ? 'grid grid-cols-1 gap-3' : 'flex flex-wrap items-end gap-2';
  const w = stacked ? 'w-full' : '';
  return (
    <div className={wrap}>
      <Labeled label="Type" className={w}>
        <select aria-label="Type" className={cn(control, stacked && 'h-11', w)} value={f.type} onChange={(e) => set({ type: e.target.value as LibraryFilters['type'] })}>
          <option value="all">Blogs + social</option>
          <option value="blog">Blogs</option>
          <option value="social">Social posts</option>
        </select>
      </Labeled>
      <Labeled label="Date" className={w}>
        <select aria-label="Date" className={cn(control, stacked && 'h-11', w)} value={f.date} onChange={(e) => {
          const date = e.target.value as LibraryFilters['date'];
          set({ date, month: date === 'month' ? (f.month || months[0].value) : f.month });
        }}>
          {DATE_PRESETS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
      </Labeled>
      {f.date === 'month' && (
        <Labeled label="Month" className={w}>
          <select aria-label="Month" className={cn(control, stacked && 'h-11', w)} value={f.month} onChange={(e) => set({ month: e.target.value })}>
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </Labeled>
      )}
      {f.date === 'custom' && (
        <>
          <Labeled label="From" className={w}>
            <input aria-label="From date" type="date" className={cn(control, stacked && 'h-11', w)} value={f.from} onChange={(e) => set({ from: e.target.value })} />
          </Labeled>
          <Labeled label="To" className={w}>
            <input aria-label="To date" type="date" className={cn(control, stacked && 'h-11', w)} value={f.to} onChange={(e) => set({ to: e.target.value })} />
          </Labeled>
        </>
      )}
      {f.date !== 'any' && (
        <Labeled label="Date of" className={w}>
          <select aria-label="Date of" className={cn(control, stacked && 'h-11', w)} value={f.dateField} onChange={(e) => set({ dateField: e.target.value as LibraryFilters['dateField'] })}>
            {DATE_FIELDS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </Labeled>
      )}
      <Labeled label="Target page / listing" className={w}>
        <select aria-label="Target page or listing" className={cn(control, stacked && 'h-11', w, !stacked && 'max-w-[220px]')} value={f.target} onChange={(e) => set({ target: e.target.value })}>
          <option value="">Any page</option>
          {withCurrent(options.target_pages, f.target).map((o) => (
            <option key={o.value} value={o.value}>{(o.label || targetLabel(o.value)) + (o.count ? ` (${o.count})` : '')}</option>
          ))}
        </select>
      </Labeled>
      <Labeled label="Keyword" className={w}>
        <select aria-label="Keyword" className={cn(control, stacked && 'h-11', w, !stacked && 'max-w-[200px]')} value={f.keyword} onChange={(e) => set({ keyword: e.target.value })}>
          <option value="">Any keyword</option>
          {withCurrent(options.keywords, f.keyword).map((o) => (
            <option key={o.value} value={o.value}>{o.value + (o.count ? ` (${o.count})` : '')}</option>
          ))}
        </select>
      </Labeled>
      <Labeled label="Status" className={w}>
        <select aria-label="Status" className={cn(control, stacked && 'h-11', w)} value={one(f.status)} onChange={(e) => set({ status: e.target.value ? [e.target.value] : [] })}>
          <option value="">Any status</option>
          {STATUS_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </Labeled>
      <Labeled label="WordPress" className={w}>
        <select aria-label="WordPress state" className={cn(control, stacked && 'h-11', w)} value={one(f.wp)} onChange={(e) => set({ wp: e.target.value ? [e.target.value] : [] })}>
          <option value="">Any</option>
          {WP_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </Labeled>
      <Labeled label="Channel" className={w}>
        <select aria-label="Channel" className={cn(control, stacked && 'h-11', w)} value={one(f.channel)} onChange={(e) => set({ channel: e.target.value ? [e.target.value] : [] })}>
          <option value="">Any channel</option>
          {withCurrent(options.channels, one(f.channel)).map((o) => <option key={o.value} value={o.value}>{channelName(o.value) + (o.count ? ` (${o.count})` : '')}</option>)}
        </select>
      </Labeled>
      <Labeled label="Source" className={w}>
        <select aria-label="Source" className={cn(control, stacked && 'h-11', w)} value={one(f.source)} onChange={(e) => set({ source: e.target.value ? [e.target.value] : [] })}>
          <option value="">Any source</option>
          {SOURCE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </Labeled>
      <Labeled label="Site" className={w}>
        <select aria-label="Site scope" className={cn(control, stacked && 'h-11', w)} value={f.scope} onChange={(e) => set({ scope: e.target.value as LibraryFilters['scope'] })}>
          <option value="domain">Viewing domain</option>
          <option value="all">All domains</option>
          <option value="unassigned">Unassigned only</option>
        </select>
      </Labeled>
      <Labeled label="Sort" className={w}>
        <select aria-label="Sort" className={cn(control, stacked && 'h-11', w)} value={f.sort} onChange={(e) => set({ sort: e.target.value as LibraryFilters['sort'] })}>
          {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </Labeled>
    </div>
  );
}

export function ViewChips({ view, facets, onPick }: {
  view: LibraryView; facets: Record<string, number> | null; onPick: (v: LibraryView) => void;
}) {
  return (
    <div role="tablist" aria-label="Saved views" className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
      {VIEWS.map((v) => {
        const n = facets ? facets[v.key] : null;
        const on = v.key === view;
        return (
          <button
            key={v.key}
            role="tab"
            aria-selected={on}
            onClick={() => onPick(v.key)}
            className={cn(
              'inline-flex min-h-[44px] sm:min-h-[36px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-xs transition-colors',
              on ? 'border-ai bg-ai/10 font-semibold text-ai' : 'border-border-default bg-surface text-text-secondary hover:text-text-primary',
            )}
          >
            {v.label}
            {v.key !== 'all' && n !== null && (
              <span className={cn('font-mono tabular-nums', v.tone === 'danger' && n > 0 ? 'text-danger' : '')}>{n}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function SearchBox({ value, onChange }: { value: string; onChange: (q: string) => void }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  useEffect(() => {
    if (local === value) return;
    const t = setTimeout(() => onChange(local), 300);
    return () => clearTimeout(t);
  }, [local, value, onChange]);
  return (
    <input
      type="search"
      aria-label="Search title, slug, keyword, target page or copy"
      placeholder="Search title, slug, keyword, copy…"
      value={local}
      maxLength={120}
      onChange={(e) => setLocal(e.target.value)}
      className="min-h-[44px] w-full min-w-0 flex-1 rounded-md border border-border-default bg-surface px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30 sm:min-h-[36px] sm:min-w-[200px]"
    />
  );
}

export function FilterSheet({ open, onClose, children, count }: { open: boolean; onClose: () => void; children: ReactNode; count: number }) {
  const ref = useFocusTrap<HTMLDivElement>(open, onClose);
  if (!open) return null;
  return (
    <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Filters" className="fixed inset-0 z-50 outline-none">
      <div className="absolute inset-0 bg-scrim/40" onClick={onClose} aria-hidden />
      <div className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-xl border-t border-border-default bg-surface shadow-lg">
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <h2 className="text-sm font-semibold text-text-primary">Filters{count ? ` (${count})` : ''}</h2>
          <button onClick={onClose} className="min-h-[44px] rounded px-3 text-sm text-text-secondary" aria-label="Close filters">Close</button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-3">{children}</div>
        <footer className="border-t border-border-subtle px-4 py-3">
          <button onClick={onClose} className="h-11 w-full rounded-md bg-neutral-10 text-sm font-medium text-neutral-0">Show results</button>
        </footer>
      </div>
    </div>
  );
}

export function ActiveChips({ f, set, clearAll, total }: { f: LibraryFilters; set: (p: Partial<LibraryFilters>) => void; clearAll: () => void; total: number | null }) {
  const chips: Array<{ k: string; label: string; clear: Partial<LibraryFilters> }> = [];
  if (f.q) chips.push({ k: 'q', label: `Search: ${f.q}`, clear: { q: '' } });
  if (f.type !== 'all') chips.push({ k: 'type', label: f.type === 'blog' ? 'Blogs' : 'Social posts', clear: { type: 'all' } });
  if (f.date !== 'any') chips.push({ k: 'date', label: `Date: ${DATE_PRESETS.find((d) => d.key === f.date)?.label.replace('…', '')}${f.date === 'month' ? ' ' + f.month : ''}${f.date === 'custom' ? ` ${f.from || '…'} to ${f.to || '…'}` : ''}`, clear: { date: 'any', month: '', from: '', to: '' } });
  if (f.target) chips.push({ k: 'target', label: `Page: ${targetLabel(f.target)}`, clear: { target: '' } });
  if (f.keyword) chips.push({ k: 'kw', label: `Keyword: ${f.keyword}`, clear: { keyword: '' } });
  if (f.status.length) chips.push({ k: 'st', label: `Status: ${f.status.map((s) => STATUS_OPTIONS.find((o) => o.key === s)?.label ?? s).join(', ')}`, clear: { status: [] } });
  if (f.wp.length) chips.push({ k: 'wp', label: `WordPress: ${f.wp.map((s) => WP_OPTIONS.find((o) => o.key === s)?.label ?? s).join(', ')}`, clear: { wp: [] } });
  if (f.channel.length) chips.push({ k: 'ch', label: `Channel: ${f.channel.map(channelName).join(', ')}`, clear: { channel: [] } });
  if (f.source.length) chips.push({ k: 'src', label: `Source: ${f.source.map((s) => SOURCE_OPTIONS.find((o) => o.key === s)?.label ?? s).join(', ')}`, clear: { source: [] } });
  if (f.scope !== 'domain') chips.push({ k: 'scope', label: f.scope === 'all' ? 'All domains' : 'Unassigned only', clear: { scope: 'domain' } });
  return (
    <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-text-secondary">
      {total !== null && <span className="font-mono tabular-nums">{total} {total === 1 ? 'item' : 'items'}</span>}
      {chips.map((c) => (
        <button key={c.k} onClick={() => set(c.clear)} className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-inset px-2 py-1 hover:text-text-primary" aria-label={`Remove filter ${c.label}`}>
          <span className="truncate">{c.label}</span><span aria-hidden className="text-text-secondary">✕</span>
        </button>
      ))}
      {chips.length > 0 && <button onClick={clearAll} className="px-1 font-medium text-ai hover:underline">Clear all</button>}
    </div>
  );
}

